import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { permissionAPI } from "../utils/api";
import {
  NAV_SECTIONS,
  resolveRoutePermissionKey,
} from "../permissions/navCatalog";

const PermissionContext = createContext(null);

function applyPermissionPayload(setters, data) {
  setters.setGrantedSet(new Set(data.permissions || []));
  setters.setBypass(Boolean(data.bypass));
  setters.setPermissionsVersion(data.permissionsVersion ?? 0);
}

export function PermissionProvider({ children }) {
  const {
    isUserAuthenticated,
    dbUser,
    sessionPermissions,
    isEvalrisOperator,
    isFetching: authFetching,
  } = useAuth();
  const [grantedSet, setGrantedSet] = useState(() => new Set());
  const [bypass, setBypass] = useState(false);
  const [permissionsVersion, setPermissionsVersion] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const hasLoadedOnceRef = useRef(false);

  const clearPermissions = useCallback(() => {
    setGrantedSet(new Set());
    setBypass(false);
    setPermissionsVersion(0);
    setVerifyError(null);
    setInitialLoading(false);
    setRefreshing(false);
    hasLoadedOnceRef.current = false;
  }, []);

  const refreshPermissions = useCallback(async () => {
    if (!isUserAuthenticated || !dbUser || dbUser.status !== "ACTIVE") {
      clearPermissions();
      return { ok: false };
    }

    const isSubsequent = hasLoadedOnceRef.current;
    if (isSubsequent) {
      setRefreshing(true);
    } else {
      setInitialLoading(true);
    }
    setVerifyError(null);
    try {
      const res = await permissionAPI.getMe();
      if (!res.data?.success) {
        throw new Error("Permission verify failed");
      }
      const data = res.data.data;
      applyPermissionPayload(
        { setGrantedSet, setBypass, setPermissionsVersion },
        data
      );
      return { ok: true, data };
    } catch (err) {
      setGrantedSet(new Set());
      setBypass(false);
      setVerifyError(err);
      return { ok: false, err };
    } finally {
      if (isSubsequent) {
        setRefreshing(false);
      } else {
        setInitialLoading(false);
        hasLoadedOnceRef.current = true;
      }
    }
  }, [isUserAuthenticated, dbUser, clearPermissions]);

  useEffect(() => {
    hasLoadedOnceRef.current = false;
    setInitialLoading(true);
    setRefreshing(false);
    setVerifyError(null);
  }, [dbUser?.ROWID, dbUser?.id, dbUser?.user_id]);

  useEffect(() => {
    if (authFetching) return;

    if (!isUserAuthenticated || !dbUser) {
      clearPermissions();
      return;
    }

    if (dbUser.status !== "ACTIVE") {
      clearPermissions();
      return;
    }

    if (sessionPermissions) {
      applyPermissionPayload(
        { setGrantedSet, setBypass, setPermissionsVersion },
        sessionPermissions
      );
      setVerifyError(null);
      setInitialLoading(false);
      hasLoadedOnceRef.current = true;
      return;
    }

    const role = String(dbUser.role || "").toUpperCase();
    const needsPermissionBootstrap = isEvalrisOperator || role === "SUPER_ADMIN";

    if (needsPermissionBootstrap) {
      refreshPermissions();
      return;
    }

    clearPermissions();
  }, [
    authFetching,
    isUserAuthenticated,
    dbUser,
    sessionPermissions,
    isEvalrisOperator,
    refreshPermissions,
    clearPermissions,
  ]);

  const can = useCallback(
    (key) => {
      if (!key) return false;
      if (bypass) return true;
      return grantedSet.has(key);
    },
    [bypass, grantedSet]
  );

  const canRoute = useCallback(
    (pathname) => {
      if (bypass) return true;
      const routeKey = resolveRoutePermissionKey(pathname);
      if (!routeKey) return false;
      return grantedSet.has(routeKey);
    },
    [bypass, grantedSet]
  );

  const visibleNavSections = useMemo(() => {
    return NAV_SECTIONS.map((section) => {
      if (!can(section.sectionKey)) return null;
      const items = section.items.filter((item) => can(item.navKey));
      if (!items.length) return null;
      return { ...section, items };
    }).filter(Boolean);
  }, [can]);

  const loading = initialLoading || refreshing;

  const value = useMemo(
    () => ({
      can,
      canRoute,
      bypass,
      permissionsVersion,
      visibleNavSections,
      loading,
      initialLoading,
      refreshing,
      verifyError,
      refreshPermissions,
      isReady: Boolean(
        isUserAuthenticated &&
          dbUser &&
          dbUser.status === "ACTIVE" &&
          !initialLoading
      ),
    }),
    [
      can,
      canRoute,
      bypass,
      permissionsVersion,
      visibleNavSections,
      loading,
      initialLoading,
      refreshing,
      verifyError,
      refreshPermissions,
      isUserAuthenticated,
      dbUser,
    ]
  );

  return (
    <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
  );
}

export function usePermissions() {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error("usePermissions must be used within PermissionProvider");
  }
  return ctx;
}
